import { useMemo, useState } from 'react';
import { Table, Typography, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ArticleKind } from '@nagoya-cat/shared';
import { api, type ArticleRow } from '../api/client';
import FilterBar from './article-list/FilterBar';
import { buildColumns } from './article-list/columns';

export default function ArticleListPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [categoryId, setCategoryId] = useState<string>();
  const [authorId, setAuthorId] = useState<string>();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });
  const create = useMutation({
    mutationFn: () => api.createArticle('未命名草稿', (kind as ArticleKind) ?? 'post'),
    onSuccess: ({ id }) => navigate(`/articles/${id}/edit`),
  });

  const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
  Object.entries({ title, kind, status, categoryId, authorId }).forEach(([k, v]) => {
    if (v) params[k] = v;
  });
  const { data, isLoading } = useQuery({
    queryKey: ['articles', params],
    queryFn: () => api.listArticles(params),
  });

  const reset = () => setPage(1);
  const columns = useMemo(() => buildColumns((id) => navigate(`/articles/${id}/edit`)), [navigate]);
  const total = data?.total ?? 0;

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <Typography.Title level={3} className="!mb-1 !font-semibold">
            文章與頁面
          </Typography.Title>
          <p className="m-0 text-sm text-gray-400">
            {isLoading ? '載入中…' : `共 ${total} 筆`}
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          loading={create.isPending}
          onClick={() => create.mutate()}
          className="shadow-sm"
        >
          {kind === 'page' ? '新增頁面' : '新增文章'}
        </Button>
      </div>

      <FilterBar
        showCategory={kind !== 'page'}
        categories={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        authors={(users.data ?? []).map((u) => ({ value: u.id, label: u.displayName }))}
        onTitle={(v) => { setTitle(v); reset(); }}
        onKind={(v) => { setKind(v); reset(); }}
        onCategory={(v) => { setCategoryId(v); reset(); }}
        onAuthor={(v) => { setAuthorId(v); reset(); }}
        onStatus={(v) => { setStatus(v); reset(); }}
      />

      <div className="overflow-hidden rounded-xl border border-black/5 bg-white shadow-sm">
        <Table<ArticleRow>
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data?.items ?? []}
          rowClassName="group/row"
          styles={{
            header: { cell: { background: '#fafafa', fontWeight: 600, color: '#6b7280' } },
            body: { cell: { paddingTop: 14, paddingBottom: 14 } },
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: setPage,
            showTotal: (t) => `共 ${t} 筆`,
            className: 'px-4',
          }}
        />
      </div>
    </div>
  );
}
